import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import {
  PageBg,
  Card,
  LabelMeta,
  LoadingState,
  EmptyState,
  theme,
  formatDateTime,
} from "./ApplicantDetail/components/ui";

function ViewFeedback() {
  const { jobApplicantId } = useParams();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await axiosInstance.get(
          API.feedback.getByJobApplicantId(jobApplicantId)
        );
        setFeedback(response.data);
      } catch (error) {
        console.error("Error fetching feedback:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, [jobApplicantId]);

  const BackButton = (
    <button
      type="button"
      onClick={() => navigate(-1)}
      style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        color: theme.accentDeep,
        padding: "8px 18px",
        borderRadius: "40px",
        fontSize: "0.85rem",
        fontWeight: 500,
        marginBottom: "1rem",
        cursor: "pointer",
        boxShadow: theme.shadowCard,
        fontFamily: theme.fontFamily,
      }}
    >
      ← Back
    </button>
  );

  if (loading) {
    return (
      <PageBg style={{ marginLeft: "250px", marginTop: "20px" }}>
        {BackButton}
        <LoadingState label="Loading feedback..." />
      </PageBg>
    );
  }

  if (!feedback) {
    return (
      <PageBg style={{ marginLeft: "250px", marginTop: "20px" }}>
        {BackButton}
        <EmptyState
          icon="📝"
          title="No feedback found"
          hint="Feedback for this candidate hasn't been submitted yet."
        />
      </PageBg>
    );
  }

  const scoreFields = [
    { key: "technicalSkills", label: "Technical Skills", icon: "💻" },
    { key: "communication", label: "Communication", icon: "🗣️" },
    { key: "problemSolving", label: "Problem Solving", icon: "🧩" },
    { key: "experienceFit", label: "Experience Fit", icon: "📚" },
    { key: "teamFit", label: "Team Fit", icon: "🤝" },
    { key: "overallRating", label: "Overall Rating", icon: "⭐" },
  ];

  const numericScores = scoreFields
    .map((f) => feedback[f.key])
    .filter((s) => typeof s === "number");
  const average =
    numericScores.length > 0
      ? numericScores.reduce((s, v) => s + v, 0) / numericScores.length
      : null;

  const avgColor =
    average == null
      ? theme.textMuted
      : average >= 8
      ? "#22c55e"
      : average >= 5
      ? "#f59e0b"
      : "#ef4444";

  return (
    <PageBg style={{ marginLeft: "250px", marginTop: "20px" }}>
      {BackButton}

      {/* Hero with average score */}
      <div
        style={{
          background: theme.navyGradient,
          borderRadius: "28px",
          padding: "20px 28px",
          marginBottom: "1.5rem",
          color: "white",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          boxShadow: theme.shadowHero,
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              opacity: 0.85,
              fontWeight: 600,
            }}
          >
            Interview Feedback
          </div>
          <div
            style={{
              fontSize: "1.6rem",
              fontWeight: 600,
              marginTop: "4px",
            }}
          >
            📝 Candidate Evaluation Summary
          </div>
          {(feedback.lastModifiedOn || feedback.createdOn) && (
            <div
              style={{
                fontSize: "0.85rem",
                opacity: 0.9,
                marginTop: "6px",
              }}
            >
              {/* Per Mudassir: use lastModifiedOn (actual submission time)
                  rather than createdOn (default-seeded with interview time). */}
              Submitted: {formatDateTime(feedback.lastModifiedOn || feedback.createdOn)}
            </div>
          )}
        </div>
        <div
          style={{
            textAlign: "center",
            background: "rgba(255,255,255,0.12)",
            borderRadius: "24px",
            padding: "14px 28px",
            border: "1.5px solid rgba(255,255,255,0.25)",
          }}
        >
          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              opacity: 0.85,
            }}
          >
            Average Score
          </div>
          <div
            style={{
              fontSize: "2.6rem",
              fontWeight: 700,
              color: avgColor,
              lineHeight: 1.1,
              marginTop: "4px",
            }}
          >
            {average == null ? "—" : average.toFixed(1)}
            <span style={{ fontSize: "1rem", fontWeight: 500, opacity: 0.85 }}>
              /10
            </span>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1.5rem",
        }}
      >
        {/* LEFT — scores */}
        <Card title="Evaluation Scores" icon="📊">
          <LabelMeta style={{ marginBottom: "10px" }}>1 – 10 scale</LabelMeta>
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {scoreFields.map((f) => (
              <ScoreRow
                key={f.key}
                icon={f.icon}
                label={f.label}
                value={feedback[f.key]}
              />
            ))}
          </div>
        </Card>

        {/* RIGHT — insights */}
        <Card title="Interview Insights" icon="🔍">
          <InsightBlock
            label="💪 Strengths observed"
            value={feedback.strengths}
            accent="#2b7e3a"
          />
          <InsightBlock
            label="⚠️ Concerns / Areas for improvement"
            value={feedback.concerns}
            accent="#c26b2c"
          />
          <InsightBlock
            label="🧠 Key behavioral & technical observations"
            value={feedback.keyObservations}
            accent="#1677ff"
          />
        </Card>
      </div>
    </PageBg>
  );
}

function ScoreRow({ icon, label, value }) {
  const numeric = typeof value === "number";
  const color = !numeric
    ? theme.textMuted
    : value >= 8
    ? "#22c55e"
    : value >= 5
    ? "#f59e0b"
    : "#ef4444";
  const pct = numeric ? Math.min(100, Math.max(0, (value / 10) * 100)) : 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.9rem",
          marginBottom: "5px",
        }}
      >
        <span style={{ color: theme.text, fontWeight: 500 }}>
          <span style={{ marginRight: "8px" }}>{icon}</span>
          {label}
        </span>
        <span
          style={{
            fontWeight: 700,
            color,
            fontSize: "0.95rem",
            minWidth: "40px",
            textAlign: "right",
          }}
        >
          {numeric ? `${value}/10` : "N/A"}
        </span>
      </div>
      <div
        style={{
          background: "#e4eaf2",
          borderRadius: "20px",
          height: "6px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            background: color,
            height: "100%",
            transition: "width 0.4s ease",
          }}
        />
      </div>
    </div>
  );
}
ScoreRow.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.number,
};

function InsightBlock({ label, value, accent }) {
  return (
    <div style={{ marginBottom: "16px" }}>
      <LabelMeta>{label}</LabelMeta>
      <div
        style={{
          background: "#fafdfb",
          padding: "12px 14px",
          borderRadius: "18px",
          marginTop: "6px",
          fontSize: "0.9rem",
          lineHeight: 1.5,
          color: theme.text,
          borderLeft: `3px solid ${accent || "#7c9bc0"}`,
          whiteSpace: "pre-wrap",
          minHeight: "60px",
        }}
      >
        {value || (
          <span style={{ color: theme.textLabel, fontStyle: "italic" }}>
            No notes provided.
          </span>
        )}
      </div>
    </div>
  );
}
InsightBlock.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
  accent: PropTypes.string,
};

export default ViewFeedback;
